// src/services/ExcelReader.js

import * as XLSX from 'xlsx';

class ExcelReaderService {
    constructor() {
        this.dataFolder = '/data/'; // public/data/ klasörü
    }

    // Fahrenheit'ten Celsius'a dönüştür
    convertFahrenheitToCelsius(fahrenheitValue) {
        if (!fahrenheitValue || isNaN(fahrenheitValue)) return 0;
        const fahrenheit = parseFloat(fahrenheitValue);
        const celsius = (fahrenheit - 32) * 5 / 9;

        if (Math.abs(celsius) > 200) {
            console.warn(`⚠️ Anormal sıcaklık: ${celsius.toFixed(1)}°C`);
            return 0;
        }

        return celsius.toFixed(1);
    }

    // Lokasyon belirleme
    determineLocation(lat, long) {
        const zones = ['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10',
            'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10',
            'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10'];
        return zones[Math.floor(Math.random() * zones.length)];
    }

    toNumber(value) {
        if (typeof value === 'number') {
            return value;
        }
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    parseUtcDateTime(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value === 'number' && !Number.isNaN(value)) {
            const excelEpoch = Date.UTC(1899, 11, 30);
            return new Date(excelEpoch + value * 86400000);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) {
                return null;
            }
            const numericValue = Number(trimmed);
            if (!Number.isNaN(numericValue)) {
                const excelEpoch = Date.UTC(1899, 11, 30);
                return new Date(excelEpoch + numericValue * 86400000);
            }
        }
        const rawDate = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(rawDate.getTime())) {
            return null;
        }
        return new Date(
            Date.UTC(
                rawDate.getFullYear(),
                rawDate.getMonth(),
                rawDate.getDate(),
                rawDate.getHours(),
                rawDate.getMinutes(),
                rawDate.getSeconds()
            )
        );
    }

    normalizeStatus(value) {
        if (!value) {
            return 'normal';
        }
        const normalized = String(value).trim().toLowerCase();
        if (
            normalized.includes('attention') ||
            normalized.includes('warning') ||
            normalized.includes('alarm') ||
            normalized.includes('critical') ||
            normalized.includes('uyarı')
        ) {
            return 'attention';
        }
        return 'normal';
    }

    // Tek bir Excel dosyasını oku
    normalizeFileName(fileName, folder) {
        const trimmedFolder = folder ? folder.replace(/^\/+|\/+$/g, '') : '';
        const sanitizedFileName = fileName.replace(/^\/+/, '');

        if (!trimmedFolder) {
            return sanitizedFileName;
        }

        const folderPrefix = `${trimmedFolder}/`;
        if (sanitizedFileName.toLowerCase().startsWith(folderPrefix.toLowerCase())) {
            return sanitizedFileName;
        }

        return `${folderPrefix}${sanitizedFileName}`;
    }

    async readExcelFile(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            // Dosya adından cihaz bilgisi
            const baseFileName = normalizedFileName.split('/').pop() || normalizedFileName;
            const deviceName = baseFileName
                .replace(/\.(xlsx|xls|csv)$/i, '')
                .replace(/^Cihaz-?/i, '');

            // Metadata çıkar (Takım-Saha-Tarih-Not formatı)
            const parts = deviceName.split('-');
            const metadata = {
                device: deviceName,
                team: parts[0] || 'Unknown Team',
                field: parts[1] || 'Unknown Field',
                date: parts[2] || new Date().toISOString().split('T')[0],
                note: parts[3] || ''
            };

            // Veriyi dönüştür
            const mappedData = jsonData.map((row, index) => {
                const rawTemp = this.toNumber(row['field1(Temperature ºF )'] || row.field1 || 0);
                const rawSoilTemp = this.toNumber(row['field5(RS485 EC Soil Temperature ºF )'] || row.field5 || 0);

                return {
                    id: index + 1,
                    created_at: row.created_at || new Date().toISOString(),
                    device: metadata.device,
                    location: this.determineLocation(row.lat, row.long),
                    team: metadata.team,
                    field: metadata.field,
                    uploadDate: metadata.date,
                    note: metadata.note,
                    airTempC: rawTemp,
                    humidity: row['field2(Humidity)'] || row.field2 || 0,
                    windSpeed: row['field3(Wind Speed-W1)'] || row.field3 || 0,
                    lux: row['field4(Illumination)'] || row.field4 || 0,
                    co2: row['field9(Carbon Dioxide-C1)'] || row.field9 || 0,
                    soilTempC: rawSoilTemp,
                    soilMoisture: row['field6(RS485 EC Soil Moisture)'] || row.field6 || 0,
                    soilEC: row['field7(RS485 Soil EC)'] || row.field7 || 0,
                    soilPH: row['field8(RS485 Soil PH)'] || row.field8 || 0,
                    status: row.status || 'Online',
                    lat: row.lat || 0,
                    long: row.long || 0,
                    dataQuality: (Math.abs(rawTemp) > 200 || Math.abs(rawSoilTemp) > 200) ? 'warning' : 'good'
                };
            });

            return {
                success: true,
                device: metadata.device,
                metadata,
                data: mappedData,
                recordCount: mappedData.length
            };

        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                device: fileName,
                error: error.message,
                data: []
            };
        }
    }

    async readWorksheetData(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            return {
                success: true,
                data: jsonData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readTemperatureTimeSeries(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) =>
                String(value || '')
                    .trim()
                    .toLowerCase()
                    .replace(/℉|°f/g, 'f')
                    .replace(/℃|°c/g, 'c')
                    .replace(/[^a-z0-9]/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const noValue = getValue(row, ['No.']);
                const timeValue = getValue(row, ['Time']);
                const temperatureCValue = getValue(row, [
                    'Temperature(°C)',
                    'Temperature(C)',
                    'Temperature(℃)',
                    'Temperature(Celsius)'
                ]);
                const temperatureFValue = getValue(row, [
                    'Temperature(℉)',
                    'Temperature(°F)',
                    'Temperature(F)',
                    'Temperature(Fahrenheit)'
                ]);
                const rawCelsius = temperatureCValue !== '' ? this.toNumber(temperatureCValue) : null;
                const rawFahrenheit = temperatureFValue !== '' ? this.toNumber(temperatureFValue) : null;
                const temperatureC = rawCelsius !== null
                    ? rawCelsius
                    : (rawFahrenheit !== null ? this.toNumber(this.convertFahrenheitToCelsius(rawFahrenheit)) : 0);

                return {
                    id: `temp-series-${normalizedFileName}-${index + 1}`,
                    no: this.toNumber(noValue) || index + 1,
                    timestamp: this.parseUtcDateTime(timeValue),
                    temperatureC
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readHumidityTimeSeries(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) =>
                String(value || '')
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const noValue = getValue(row, ['No.']);
                const timeValue = getValue(row, ['Time']);
                const humidityValue = getValue(row, ['Humidity(%)', 'Humidity']);

                return {
                    id: `humidity-series-${normalizedFileName}-${index + 1}`,
                    no: this.toNumber(noValue) || index + 1,
                    timestamp: this.parseUtcDateTime(timeValue),
                    humidity: this.toNumber(humidityValue)
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readIlluminationTimeSeries(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) =>
                String(value || '')
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const noValue = getValue(row, ['No.']);
                const timeValue = getValue(row, ['Time']);
                const illuminationValue = getValue(row, ['Illumination', 'Lux']);

                return {
                    id: `illumination-series-${normalizedFileName}-${index + 1}`,
                    no: this.toNumber(noValue) || index + 1,
                    timestamp: this.parseUtcDateTime(timeValue),
                    illumination: this.toNumber(illuminationValue)
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readUniteFile(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const mappedData = jsonData.map((row, index) => {
                const dateValue = (() => {
                    if (row['Tarih/Saat'] !== undefined && row['Tarih/Saat'] !== '') {
                        return row['Tarih/Saat'];
                    }
                    if (row['Tarih Saat'] !== undefined && row['Tarih Saat'] !== '') {
                        return row['Tarih Saat'];
                    }
                    if (row['Tarih'] !== undefined && row['Tarih'] !== '') {
                        return row['Tarih'];
                    }
                    const dateKey = Object.keys(row).find((key) => {
                        if (!key) {
                            return false;
                        }
                        const normalized = key.toLowerCase().replace(/\s+/g, '');
                        return (
                            normalized === 'tarih/saat' ||
                            normalized === 'tarih-saat' ||
                            normalized === 'tarihsaat'
                        );
                    }) || Object.keys(row).find((key) => {
                        if (!key) {
                            return false;
                        }
                        const lowerKey = key.toLowerCase();
                        return lowerKey.includes('tarih') && lowerKey.includes('saat');
                    });
                    return dateKey ? row[dateKey] : null;
                })();
                const timestamp = this.parseUtcDateTime(dateValue);
                const unitName = row['Ünite'] || `Ünite ${index + 1}`;
                const location = row['Lokasyon'] || '-';
                const temperature = this.toNumber(row['Toprak Sıc.']);
                const humidity = this.toNumber(row['Toprak Nem']);
                const light = this.toNumber(row['PPFD']);
                const status = this.normalizeStatus(row['Durum']);

                return {
                    id: `unite-${index + 1}`,
                    index: index + 1,
                    timestamp,
                    unitName,
                    location,
                    temperature,
                    humidity,
                    light,
                    status
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readHeatingFile(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const timestampValue = getValue(row, ['Tarih/Saat', 'Tarih Saat', 'Tarih']);
                const system = getValue(row, ['Sistem']);
                const inletTemp = this.toNumber(getValue(row, ['Giriş Sıc.', 'Giriş Sıc']));
                const outletTemp = this.toNumber(getValue(row, ['Çıkış Sıc.', 'Çıkış Sıc']));
                const deltaT = this.toNumber(getValue(row, ['Delta T', 'DeltaT']));
                const pressure = this.toNumber(getValue(row, ['Basınç', 'Basinc']));
                const statusRaw = getValue(row, ['Durum', 'Status']);

                return {
                    id: `heating-${normalizedFileName}-${index + 1}`,
                    timestamp: this.parseUtcDateTime(timestampValue),
                    system,
                    inletTemp,
                    outletTemp,
                    deltaT,
                    pressure,
                    status: this.normalizeStatus(statusRaw)
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readIrrigationValveList(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) =>
                String(value || '')
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .replace(/[-_]/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const name = getValue(row, ['Vana', 'Vana Adı', 'Vana No', 'VanaNo']);
                const location = getValue(row, ['Lokasyon', 'Konum']);
                const statusRaw = getValue(row, ['Durum', 'Status']);

                return {
                    id: `valve-${normalizedFileName}-${index + 1}`,
                    name: name || `Vana ${index + 1}`,
                    location: location || '-',
                    status: this.normalizeStatus(statusRaw)
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    async readIrrigationSoilByLocation(fileName, folder = '') {
        try {
            const normalizedFileName = this.normalizeFileName(fileName, folder);
            const response = await fetch(`${this.dataFolder}${normalizedFileName}`);

            if (!response.ok) {
                throw new Error(`Dosya bulunamadı: ${fileName}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const normalizeKey = (value) =>
                String(value || '')
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .replace(/[-_]/g, '');
            const getValue = (row, keys) => {
                for (const key of keys) {
                    if (row[key] !== undefined && row[key] !== '') {
                        return row[key];
                    }
                }
                const normalizedKeys = keys.map((key) => normalizeKey(key));
                const matchingKey = Object.keys(row).find(
                    (key) => normalizedKeys.includes(normalizeKey(key))
                );
                return matchingKey ? row[matchingKey] : '';
            };

            const mappedData = jsonData.map((row, index) => {
                const location = getValue(row, ['Lokasyon', 'Konum']);
                const temperatureValue = getValue(row, ['Toprak Sıcaklığı', 'Toprak Sicakligi', 'Toprak Sıc.']);
                const moistureValue = getValue(row, ['Toprak Nemi', 'Toprak Nem', 'Nem']);
                const lastIrrigationValue = getValue(row, ['Son Sulama', 'SonSulama', 'Son Sulama Tarihi']);
                const statusRaw = getValue(row, ['Durum', 'Status']);

                return {
                    id: `soil-${normalizedFileName}-${index + 1}`,
                    location: location || '-',
                    soilTemperature: this.toNumber(temperatureValue),
                    soilMoisture: this.toNumber(moistureValue),
                    lastIrrigation: this.parseUtcDateTime(lastIrrigationValue),
                    status: this.normalizeStatus(statusRaw)
                };
            });

            return {
                success: true,
                data: mappedData
            };
        } catch (error) {
            console.error(`❌ ${fileName} okunamadı:`, error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    // Tüm Excel dosyalarını oku
    async readAllFiles(fileList, folder = '') {
        const results = [];

        for (const fileName of fileList) {
            console.log(`📖 Okunuyor: ${fileName}`);
            const result = await this.readExcelFile(fileName, folder);
            results.push(result);
        }

        // Başarılı okumaları birleştir
        const allData = results
            .filter(r => r.success)
            .flatMap(r => r.data);

        const metadata = results
            .filter(r => r.success)
            .map(r => r.metadata);

        return {
            success: true,
            totalFiles: fileList.length,
            successfulReads: results.filter(r => r.success).length,
            failedReads: results.filter(r => !r.success).length,
            data: allData,
            metadata: metadata,
            devices: results.filter(r => r.success).map(r => r.device)
        };
    }
}

export default new ExcelReaderService();
